import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { appActions } from '../../../store';
import { useUrlSync } from '../../../hooks/useUrlSync';
import { useActiveProfile } from '../../../hooks/useActiveProfile';
import ProductList from '../../shared/ProductList/ProductList';
import CustomizeColors from '../CustomizeColors';
import styles from "./ProductMenu.module.css";
import { getThumbnailUrl } from '../../../Helpers/ThumbHelper';
import { getSwatchUrl } from '../../../Helpers/PatternHelper';
import left from './assets/left.svg';

export default function ProductMenu() {
    const [floorSearchTerm, setFloorSearchTerm] = useState('');
    const [floorColorFilter, setFloorColorFilter] = useState('');
    const [patternSearchTerm, setPatternSearchTerm] = useState('');
    const [floorProducts, setFloorProducts] = useState([]);
    const [colorSearchTerm, setColorSearchTerm] = useState('');
    const dispatch = useDispatch();
    
    useUrlSync();
    useActiveProfile();
    
    const style = useSelector(state => state.app.style);
    const size = useSelector(state => state.app.size);
    const chosenPattern = useSelector(state => state.app.chosenPattern);
    const selectedColorIndex = useSelector(state => state.app.selectedColorIndex);
    const patternColors = useSelector(state => state.app.patternColors);
    const activeSection = useSelector(state => state.app.menuActiveSection);
    const activeTab = useSelector(state => state.app.menuActiveTab);

    const setActiveTab = (tab) => {
        dispatch(appActions.setMenuActiveTab(tab));
    };

    useEffect(() => {
        fetch('https://lizard.chameleonpower.com/CDN/ProductsBySeries/8645.json')
            .then(res => res.json())
            .then(data => setFloorProducts(Array.isArray(data) ? data : []))
            .catch(err => console.error('Error fetching floor products:', err));
    }, []);

    // Apply strict filter to get filtered floor products for dropdown options
    const getStrictFilteredProducts = (products, strictFilters) => {
        if (!strictFilters || strictFilters.trim() === '') {
            return products;
        }
        return products.filter(product => {
            try {
                let expression = strictFilters;
                const fieldPattern = /(\w+)\s*(==|!=)\s*['"]([^'"]*)['"]/g;
                expression = expression.replace(fieldPattern, (match, field, operator, value) => {
                    const productValue = product[field];
                    const productValueStr = productValue !== null && productValue !== undefined 
                        ? String(productValue) 
                        : '';
                    if (operator === '==') {
                        return productValueStr.toLowerCase() === value.toLowerCase() ? 'true' : 'false';
                    } else if (operator === '!=') {
                        return productValueStr.toLowerCase() !== value.toLowerCase() ? 'true' : 'false';
                    }
                    return 'false';
                });
                expression = expression.replace(/&&/g, ' && ').replace(/\|\|/g, ' || ');
                if (/^[\s()&|truefalse]+$/i.test(expression)) {
                    // eslint-disable-next-line no-eval
                    return eval(expression);
                }
                return false;
            } catch (e) {
                return false;
            }
        });
    };

    const floorStrictFilter = "categoryName=='LVT' && warranty=='6X36'";
    const filteredFloorProducts = getStrictFilteredProducts(floorProducts, floorStrictFilter);
    const floorColorOptions = [...new Set(filteredFloorProducts.map(p => p.name).filter(Boolean))].sort();

    const handleSectionClick = (section) => {
        dispatch(appActions.setMenuActiveSection(section));
    };

    const handleBackToStartPage = () => {
        dispatch(appActions.clearChosenPattern());
    };

    const handlePatternSelect = (product) => {
        dispatch(appActions.setChosenPattern(product));
        dispatch(appActions.setStyle(product.name || product.color || 'Selected'));
        dispatch(appActions.setSize(product.descriptor || '18x18')); // Set size from pattern descriptor
        dispatch(appActions.setProductSelection([-1, -1]));
        dispatch(appActions.setCurrentView('swatch'));
        dispatch(appActions.setMenuActiveSection('customize')); // Go back to customize after selecting pattern
    };

    const handleFloorProductSelect = (product) => {
        console.log('Floor product selected:', product);
        dispatch(appActions.setSelectedFloorProduct(product));
    };

    const handleColorProductSelect = (product) => {
        console.log('Color product selected:', product);
        
        if (selectedColorIndex === null || !patternColors[selectedColorIndex]) {
            console.log('No color selected in CustomizeColors');
            return;
        }

        const selectedColor = patternColors[selectedColorIndex];
        dispatch(appActions.updatePatternColor({
            originalId: selectedColor.originalId,
            newProduct: product
        }));
        
        console.log('Updated color', selectedColor.originalId, 'to', product.id);
    };

    const getFloorFilters = () => {
        const filters = {};
        if (floorColorFilter) {
            filters.name = floorColorFilter;
        }
        return filters;
    };

    return (
        <section className={styles.productMenu}>
            {/* Tab Navigation */}
            <div className={styles.tabContainer}>
                <button
                    className={`${styles.tab} ${activeTab === 'rug' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('rug')}
                >
                    Rug
                </button>
                <button
                    className={`${styles.tab} ${activeTab === 'floor' ? styles.activeTab : ''}`}
                    onClick={() => setActiveTab('floor')}
                >
                    Floor
                </button>
            </div>

            {/* Rug Tab Content */}
            {activeTab === 'rug' && (
                <>
                    <div className={styles.backHeader}>
                        <button 
                            className={styles.backButton}
                            onClick={handleBackToStartPage}
                            title="Back to start"
                        >
                            <span className={styles.backArrow}>
                                <img src={left} width="auto" height="15" alt="arrow left"/>
                            </span>
                            <span className={styles.backText}>Custom Rug</span>
                        </button>
                    </div>

                    <div className={styles.subheader}>
                        <h2>Explore standard and custom options</h2>
                    </div>

                    <div className={styles.selectionGrid}>
                        {/* Pattern Section */}
                        <div 
                            className={`${styles.selectionCard} ${activeSection === 'pattern' ? styles.active : ''}`}
                            onClick={() => handleSectionClick('pattern')}
                            title="Select a pattern"
                        >
                            <div className={styles.cardImage}>
                                {chosenPattern ? (
                                    <img 
                                        src={getSwatchUrl(chosenPattern, patternColors) || getThumbnailUrl(chosenPattern)} 
                                        alt={chosenPattern.name}
                                        className={styles.cardImageImg}
                                    />
                                ) : (
                                    <div className={styles.stylePlaceholder}></div>
                                )}
                            </div>
                            <div className={styles.cardLabel}>Pattern</div>
                            <div className={styles.cardValue}>{chosenPattern?.name || 'Select'}</div>
                        </div>

                        {/* Customize Section */}
                        <div 
                            className={`${styles.selectionCard} ${activeSection === 'customize' ? styles.active : ''}`}
                            onClick={() => handleSectionClick('customize')}
                            title="Customize your rug"
                        >
                            <div className={styles.cardImage}>
                                <div className={styles.customizePlaceholder}>
                                    <img 
                                        src="/img/brush.svg" 
                                        alt="Customize"
                                        className={styles.sizeImage}
                                    />
                                </div>
                            </div>
                            <div className={styles.cardLabel}>Customize</div>
                            <div className={styles.cardValue}>{style || 'Select colors'}</div>
                        </div>

                        {/* Rug Shape & Size Section */}
                        <div 
                            className={`${styles.selectionCard} ${activeSection === 'rug-shape-size' ? styles.active : ''}`}
                            onClick={() => handleSectionClick('rug-shape-size')}
                            title="Choose rug shape and size"
                        >
                            <div className={styles.cardImage}>
                                <div className={styles.rugShapePlaceholder}>
                                    <img 
                                        src="/img/rug-shape.svg" 
                                        alt="Rug shape"
                                        className={styles.sizeImage}
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                    />
                                </div>
                            </div>
                            <div className={styles.cardLabel}>Rug Shape & Size</div>
                            <div className={styles.cardValue}>{size || 'Select'}</div>
                        </div>
                    </div>

                    {/* Content Area */}
                    <div className={styles.contentArea}>
                        {activeSection === 'pattern' && (
                            <div className={styles.patternContent}>
                                <h3 className={styles.patternTitle}>Pick a Pattern</h3>
                                <div className={styles.patternSearchWrapper}>
                                    <input
                                        type="text"
                                        placeholder="Pattern name or SKU (e.g., Traditional, PXMH-001)"
                                        value={patternSearchTerm}
                                        onChange={(e) => setPatternSearchTerm(e.target.value)}
                                        className={styles.patternSearchInput}
                                    />
                                    <span className={styles.patternSearchIcon} aria-hidden="true">
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                            <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" strokeWidth="2" />
                                        </svg>
                                    </span>
                                </div>
                                <ProductList 
                                    seriesId="9727"
                                    thumbSize="256"
                                    titleAttribute="name"
                                    descriptionAttribute="description"
                                    sortBy="name"
                                    sortOrder="asc"
                                    searchValue={patternSearchTerm}
                                    searchColumns={['name', 'description','sku']}
                                    onProductSelect={handlePatternSelect}
                                    selectedProductId={chosenPattern?.id}
                                />
                            </div>
                        )}

                        {activeSection === 'customize' && (
                            <div className={styles.customizeContent}>
                                <div className={styles.colorwaySection}>
                                    <p className={styles.sectionDescription}>
                                        Select a colorway and choose a new color to recolor the pattern
                                    </p>
                                    <CustomizeColors />
                                </div>

                                <div className={styles.colorSearchSection}>
                                    <div className={styles.colorSearchWrapper}>
                                        <input
                                            type="text"
                                            placeholder="Search by color"
                                            value={colorSearchTerm}
                                            onChange={(e) => setColorSearchTerm(e.target.value)}
                                            className={styles.colorSearchInput}
                                        />
                                        <span className={styles.colorSearchIcon} aria-hidden="true">
                                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                                                <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                                <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" strokeWidth="2" />
                                            </svg>
                                        </span>
                                    </div>
                                    <div className={styles.colorProductList}>
                                        <ProductList 
                                            seriesId="8623"
                                            thumbSize="80"
                                            titleAttribute="sku"
                                            descriptionAttribute=""
                                            searchValue={colorSearchTerm}
                                            searchColumns={['name', 'color', 'sku', 'description']}
                                            onProductSelect={handleColorProductSelect}
                                            gridClassName={styles.colorGrid}
                                        />
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeSection === 'rug-shape-size' && (
                            <div className={styles.rugShapeSizeContent}>
                                <div className={styles.noProfileMessage}>
                                    Rug Shape & Size options coming soon
                                </div>
                            </div>
                        )}
                    </div>
                </>
            )}

            {/* Floor Tab Content */}
            {activeTab === 'floor' && (
                <>
                    <div className={styles.header}>
                        <h1>Floor Products</h1>
                    </div>
                    <div className={styles.subheader}>
                        <h2>Browse flooring options</h2>
                    </div>

                    <div className={styles.floorFilterBar}>
                        <div className={styles.searchWrapper}>
                            <input
                                type="text"
                                placeholder="Search products..."
                                value={floorSearchTerm}
                                onChange={(e) => setFloorSearchTerm(e.target.value)}
                                className={styles.searchInput}
                            />
                            <span className={styles.searchIcon} aria-hidden="true">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                                    <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                                    <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" strokeWidth="2" />
                                </svg>
                            </span>
                        </div>
                        <select
                            value={floorColorFilter}
                            onChange={(e) => setFloorColorFilter(e.target.value)}
                            className={styles.filterDropdown}
                        >
                            <option value="">All Collections</option>
                            {floorColorOptions.map(color => (
                                <option key={color} value={color}>{color}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.contentArea}>
                        <ProductList 
                            seriesId="8645"
                            thumbSize="256"
                            titleAttribute="name"
                            descriptionAttribute="color"
                            filters={getFloorFilters()}
                            strictFilters={floorStrictFilter}
                            searchValue={floorSearchTerm}
                            searchColumns={['name', 'color', 'description', 'sku']}
                            onProductSelect={handleFloorProductSelect}
                        />
                    </div>
                </>
            )}
        </section>
    );
}
